/**
 * Admin Permission Matrix
 * -----------------------
 * Grid view of role × permission across every active assignment on the ecosystem.
 * Tenant admins are auto-scoped to their tenant; super-admin sees global.
 */
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/store';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { ShieldCheck, Grid3x3, Search } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
export default function AdminPermissionMatrix() {
  const { token } = useAuthStore();
  const hdrs = () => ({ Authorization: `Bearer ${token || getToken()}` });
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['permission-matrix'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/ecosystem-hr/permission-matrix`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
  });

  const filteredRoles = useMemo(() => {
    if (!data) return [];
    if (!search) return data.roles;
    const s = search.toLowerCase();
    return data.roles.filter((r) => r.toLowerCase().includes(s));
  }, [data, search]);

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="permission-matrix-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <span className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-emerald-500 text-white">
                <ShieldCheck className="h-6 w-6" />
              </span>
              Permission Matrix
            </h1>
            <p className="text-slate-500 mt-1">Who can do what, across every scope. Cells show how many people carry the permission.</p>
          </div>
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Filter roles…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="matrix-search" />
          </div>
        </div>

        {/* Coverage snapshot */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Grid3x3 className="h-4 w-4 text-indigo-500" /> Coverage</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-24" /> : (
              <div className="flex flex-wrap gap-2">
                {(data?.coverage || []).map((c) => (
                  <Badge key={`${c.role}-${c.scope_type}`} variant="outline" className="text-xs px-3 py-1 capitalize" data-testid={`coverage-${c.role}-${c.scope_type}`}>
                    <span className="font-semibold">{c.role.replace(/_/g, ' ')}</span>
                    <span className="mx-2 text-slate-400">·</span>
                    <span className="text-slate-600">{c.scope_type}</span>
                    <span className="ml-2 font-bold text-indigo-700">{c.count}</span>
                  </Badge>
                ))}
                {(data?.coverage || []).length === 0 && <p className="text-sm text-slate-500">No active assignments.</p>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Matrix */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Role × Permission</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {isLoading ? <Skeleton className="h-48" /> : !data || data.roles.length === 0 ? (
              <p className="text-sm text-slate-500 py-8 text-center">No data yet. Assign permissions to staff via HR → Convert-to-Teacher or similar flows.</p>
            ) : (
              <table className="min-w-full text-sm border-collapse">
                <thead>
                  <tr>
                    <th className="text-left p-2 border-b font-medium text-slate-600">Role ↓ / Permission →</th>
                    {data.permissions.map((p) => (
                      <th key={p} className="text-center p-2 border-b font-medium text-slate-600 capitalize">{p.replace(/_/g, ' ')}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRoles.map((role) => (
                    <tr key={role} className="hover:bg-slate-50" data-testid={`matrix-row-${role}`}>
                      <td className="p-2 font-medium capitalize border-b">{role.replace(/_/g, ' ')}</td>
                      {data.permissions.map((perm) => {
                        const people = data.matrix?.[role]?.[perm] || [];
                        const has = people.length > 0;
                        return (
                          <td key={perm} className="p-2 text-center border-b">
                            {has ? (
                              <Badge className={`${people.length >= 5 ? 'bg-emerald-500' : people.length >= 2 ? 'bg-indigo-500' : 'bg-slate-400'} hover:opacity-90`}>
                                {people.length}
                              </Badge>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Scopes covered */}
        {data?.scopes?.length > 0 && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Scopes in view ({data.scopes.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {data.scopes.map((s) => (
                  <Badge key={`${s.scope_type}-${s.scope_id}`} variant="outline" className="text-xs">
                    <span className="capitalize font-semibold">{s.scope_type}</span>
                    <span className="ml-2 text-slate-600">{s.label}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
